import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, RouterModule } from '@angular/router';
import { KeycloakUser } from 'app/app.type';
import { UserService } from 'app/core/user/user.service';
import { environment } from 'environments/environment';
import { KeycloakAdminService } from '../service/keycloak-admin.service';

@Component({
	selector: 'app-user-detail',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, MatSlideToggleModule, RouterModule, MatDividerModule,
    MatSnackBarModule, MatTooltipModule],
	templateUrl: './user-detail.component.html'
})
export class UserDetailComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly keycloakAdminService = inject(KeycloakAdminService);
	private readonly userService = inject(UserService);
	snackBar = inject(MatSnackBar);
	snackbarOptions = { duration: 3000 }

	clientId = environment.auth.clientId

	user: KeycloakUser | null = null;
	loading = false;
	error: string | null = null;
	processingGroups = new Set<string>();
	processingRoles = new Set<string>();

	groups: any[] = [];
	roles: any[] = [];
	allGroups: any[] = [];
	allRoles: any[] = [];

	moduleGroups: any[] = []
	brGroups: any[] = []

	moduleMap = {
		'exploration-users': 'Exploration',
		'pmo-innovation': 'Innovation',
		'user-admin': 'User Admin',
		'innovation-dashboard': 'Innovation Dashboard',
	}

	groupDescriptionMap: Record<string, string> = {
		'pmo-innovation': 'Access to the innovation module',
		'innovation-dashboard': 'Access to innovation dashboards and management reports',
		'user-admin': 'User administrator. Can manage sensitive permissions such as user management access',
		'camunda-admin': 'Process engine administrator (Camunda). Internal use',
		'settings': 'Innovation module settings and configurations',
		'performance': 'Performance team responsible for evaluating innovation initiatives',
	}

	explorationGroupId = '696ab906-2791-4c0c-8583-27f490a6857b';
	geologistGroupId = '0dd0177e-af59-41c4-80ac-3230e75c28d9';
	assistantGroupId = '6419f8a4-f4a9-475c-9c63-473ca2ecb6b5';

	removedGroupsFromDisplay = ['company', 'roles', 'camunda-admin', 'admin'];

	private readonly restrictedGroups = ['innovation-settings-users', 'user-admin'];
	private readonly requiredGroupForRestricted = 'user-admin';

	isCurrentUserAdmin(): boolean {
		return this.userService.getAllAccessGroups().includes(this.requiredGroupForRestricted);
	}

	isGroupRestricted(groupName: string): boolean {
		return this.restrictedGroups.includes(groupName) && !this.isCurrentUserAdmin();
	}

	constructor() { }

	ngOnInit(): void {
		this.loadUserFromRoute();
		this.loadAllGroupsAndRoles();
	}

	private loadAllGroupsAndRoles(): void {
		// Carregar todos os grupos disponíveis
		this.keycloakAdminService.getGroups().subscribe({
			next: (allGroups) => {
				this.allGroups = allGroups;
				this.allGroups = this.allGroups.map(group => ({
					...group,
					expanded: false,
				}))

				this.brGroups = this.allGroups.map(group => {
					if (group.name.startsWith('br')) {
						return group.subGroups;
					}
				}).filter(Boolean).flat().sort((a, b) => a.name.localeCompare(b.name));

				this.moduleGroups = this.allGroups.filter(group => !group.name.startsWith('br'))
				.filter(group => !this.removedGroupsFromDisplay.includes(group.name))
				.sort((a, b) => a.name.localeCompare(b.name));

				console.warn('Todos os grupos carregados:', allGroups);
			},
			error: (error) => {
				console.warn('Erro ao carregar todos os grupos:', error);
			}
		});

		// Carregar todos os roles disponíveis
		this.keycloakAdminService.getAvaliableRoles().subscribe({
			next: (allRoles) => {
				this.allRoles = allRoles;
				console.warn('Todos os roles carregados:', allRoles);
			},
			error: (error) => {
				console.error('Erro ao carregar todos os roles:', error);
			}
		});
	}

	hasGroup(groupId: string): boolean {
		return this.groups.some(group => group.id === groupId);
	}

	hasRole(roleName: string): boolean {
		return this.roles.some(role => role.name === roleName);
	}

	private loadUserFromRoute(): void {
		const userId = this.route.snapshot.paramMap.get('id');

		if (!userId) {
			this.error = 'ID do usuário não encontrado na rota';
			console.error('ID do usuário não encontrado na rota');
			return;
		}

		console.warn('Buscando usuário com ID:', userId);
		this.loading = true;
		this.error = null;

		this.keycloakAdminService.getUserById(userId).subscribe({
			next: (user) => {
				this.user = user;
				this.loading = false;
				console.warn('Dados do usuário encontrado:', user);
			},
			error: (error) => {
				this.error = error.message || 'Erro ao carregar usuário';
				this.loading = false;
				console.warn('Erro ao buscar usuário:', error);
			}
		});

		this.keycloakAdminService.getUserGroups(userId).subscribe({
			next: (groups) => {
				this.groups = groups;
				if (this.user) {
					this.user.groups = groups;
				}
				console.warn('Grupos do usuário encontrado:', groups);
			},
			error: (error) => {
				console.warn('Erro ao buscar grupos do usuário:', error);
			}
		});

		this.keycloakAdminService.getUserRoles(userId).subscribe({
			next: (roles) => {
				this.roles = roles;
				if (this.user) {
					this.user.roles = roles;
				}
				console.warn('Roles do usuário encontrado:', roles);
			},
			error: (error) => {
				console.warn('Erro ao buscar roles do usuário:', error);
			}
		});
	}

	changeGroupState(userId: string, groupId: string, isChecked: boolean): void {
		const group = this.allGroups.flatMap(g => g.subGroups || []).concat(this.allGroups).find(g => g.id === groupId);
		if (group && this.isGroupRestricted(group.name)) {
			this.snackBar.open('Only user-admin can manage this group', 'OK', this.snackbarOptions);
			return;
		}

		// Adicionar o grupo à lista de processamento
		this.processingGroups.add(groupId);

		const method = isChecked ? 'put' : 'delete';

		this.keycloakAdminService.manageUserGroup(userId, groupId, method).subscribe({
			next: () => {
				const action = isChecked ? 'adicionado ao' : 'removido do';
				console.warn(`Usuário ${userId} ${action} grupo ${groupId}`);

				// Atualizar lista local de grupos
				this.keycloakAdminService.getUserGroups(userId).subscribe({
					next: (groups) => {
						this.groups = groups;
						if (this.user) {
							this.user.groups = groups;
						}
						// Remover da lista de processamento
						this.processingGroups.delete(groupId);

						this.snackBar.open('Access updated', 'OK', this.snackbarOptions);
					}
				});
			},
			error: (error) => {
				const action = isChecked ? 'adicionar usuário ao' : 'remover usuário do';
				console.warn(`Erro ao ${action} grupo:`, error);
				// Remover da lista de processamento mesmo em caso de erro
				this.processingGroups.delete(groupId);
			}
		});
	}

	changeRoleState(userId: string, roleName: string, isChecked: boolean): void {
		// Adicionar o role à lista de processamento
		this.processingRoles.add(roleName);

		const method = isChecked ? 'put' : 'delete';

		this.keycloakAdminService.manageUserRole(userId, roleName, method).subscribe({
			next: () => {
				const action = isChecked ? 'adicionado ao' : 'removido do';
				console.warn(`Usuário ${userId} ${action} role ${roleName}`);

				// Atualizar lista local de roles
				this.keycloakAdminService.getUserRoles(userId).subscribe({
					next: (roles) => {
						this.roles = roles;
						if (this.user) {
							this.user.roles = roles;
						}
						// Remover da lista de processamento
						this.processingRoles.delete(roleName);

						this.snackBar.open('Access updated', 'OK', this.snackbarOptions);

					}
				});
			},
			error: (error) => {
				const action = isChecked ? 'adicionar usuário ao' : 'remover usuário do';
				console.warn(`Erro ao ${action} role:`, error);
				// Remover da lista de processamento mesmo em caso de erro
				this.processingRoles.delete(roleName);
			}
		});
	}

	isGroupProcessing(groupId: string): boolean {
		return this.processingGroups.has(groupId);
	}

	isRoleProcessing(roleName: string): boolean {
		return this.processingRoles.has(roleName);
	}

	expandGroup(groupId: string): void {
		const group = this.allGroups.find(g => g.id === groupId);
		if (group) {
			group.expanded = !group.expanded;
		}
	}
}
